import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { Send, MapPin, Phone, Mail, Sparkles, Loader2 } from "lucide-react";
import VibrantSectionHeader from "@/components/shared/VibrantSectionHeader";
import { supabase } from "@/integrations/supabase/client";

const contactInfo = [
  {
    icon: MapPin,
    title: "Address",
    content: "Saudi Arabia",
    color: "green" as const,
  },
  {
    icon: Phone,
    title: "Phone",
    content: "+966 5 737 505 48",
    color: "blue" as const,
  },
  {
    icon: Mail,
    title: "Email",
    content: "info@sdcgulf.com",
    color: "gold" as const,
  },
];

const colorStyles = {
  green: {
    bg: "bg-gradient-to-br from-eco-green/15 to-eco-green/5",
    iconBg: "bg-gradient-to-br from-eco-green to-eco-green-light",
    border: "border-eco-green/20 hover:border-eco-green/40",
    glow: "group-hover:shadow-[0_0_25px_hsl(var(--eco-green)/0.25)]",
  },
  blue: {
    bg: "bg-gradient-to-br from-sky-blue/15 to-sky-blue/5",
    iconBg: "bg-gradient-to-br from-sky-blue to-sky-blue-light",
    border: "border-sky-blue/20 hover:border-sky-blue/40",
    glow: "group-hover:shadow-[0_0_25px_hsl(var(--sky-blue)/0.25)]",
  },
  gold: {
    bg: "bg-gradient-to-br from-primary/15 to-amber-glow/5",
    iconBg: "bg-gradient-to-br from-primary to-amber-glow",
    border: "border-primary/20 hover:border-primary/40",
    glow: "group-hover:shadow-[0_0_25px_hsl(var(--primary)/0.25)]",
  },
};

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  
  // Honeypot field for bot detection (should remain empty)
  const [honeypot, setHoneypot] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Bot detection: if honeypot field is filled, silently reject
    if (honeypot) {
      // Fake success response to not alert bots
      toast({
        title: "Message Sent!",
        description: "Thank you for your inquiry. We'll get back to you soon.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
      setIsSubmitting(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('submit-contact-form', {
        body: { ...formData, honeypot },
      });

      if (error) {
        throw new Error(error.message || 'Failed to send message');
      }

      toast({
        title: "Message Sent!",
        description: data?.message || "Thank you for your inquiry. We'll get back to you soon.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Contact form error:", error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-card via-card/90 to-card" />
      <div className="absolute -left-40 top-1/3 w-80 h-80 rounded-full bg-eco-green/10 blur-3xl animate-pulse" />
      <div className="absolute -right-40 bottom-1/3 w-80 h-80 rounded-full bg-sky-blue/10 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
      <div className="absolute left-1/2 -translate-x-1/2 top-0 w-[500px] h-[500px] rounded-full bg-primary/5 blur-3xl" />

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className={`absolute w-2 h-2 rounded-full ${i % 2 === 0 ? 'bg-eco-green/30' : 'bg-primary/30'}`}
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <VibrantSectionHeader
          badge="Into Business!"
          title="Get In Touch"
          description="Send us your contact details and we'll be in touch soon."
          badgeColor="gold"
        />

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="p-8 rounded-2xl bg-gradient-to-br from-secondary/80 to-card/50 border border-border/30 backdrop-blur-sm">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-eco-green to-teal-bright flex items-center justify-center">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-bold text-foreground">Send us a message</h3>
              </div>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Input
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-14 bg-background/50 border-border/50 focus:border-eco-green/50 transition-colors"
                  />
                  <Input
                    type="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="h-14 bg-background/50 border-border/50 focus:border-eco-green/50 transition-colors"
                  />
                </div>
                <Input
                  type="tel"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-14 bg-background/50 border-border/50 focus:border-eco-green/50 transition-colors"
                />
                <Textarea
                  placeholder="Your Message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  className="min-h-[150px] bg-background/50 border-border/50 focus:border-eco-green/50 resize-none transition-colors"
                />
                {/* Honeypot field - hidden from real users, bots will fill it */}
                <div className="absolute -left-[9999px] opacity-0 pointer-events-none" aria-hidden="true">
                  <Input
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <Button 
                  type="submit" 
                  size="xl" 
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-eco-green via-teal-bright to-eco-green-light hover:from-eco-green-light hover:via-teal-bright hover:to-eco-green text-white font-semibold shadow-lg shadow-eco-green/25 hover:shadow-xl hover:shadow-eco-green/30 transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>

          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((info) => {
              const style = colorStyles[info.color];
              return (
                <div
                  key={info.title}
                  className={`group flex items-start gap-4 p-6 rounded-2xl ${style.bg} border ${style.border} backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 ${style.glow}`}
                >
                  <div className={`w-14 h-14 rounded-xl ${style.iconBg} flex items-center justify-center shrink-0 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                    <info.icon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground mb-1">{info.title}</h3>
                    <p className="text-muted-foreground">{info.content}</p>
                  </div>
                </div>
              );
            })}

            {/* Additional CTA Card */}
            <div className="p-6 rounded-2xl bg-gradient-to-r from-primary/10 via-amber-glow/10 to-primary/10 border border-primary/20 backdrop-blur-sm">
              <h3 className="font-bold text-foreground mb-2">Ready to transform your mobility?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Our team is here to help you find the perfect sustainable solution for your needs.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-eco-green/20 text-eco-green text-xs font-medium border border-eco-green/30">
                  24/7 Support
                </span>
                <span className="px-3 py-1 rounded-full bg-sky-blue/20 text-sky-blue text-xs font-medium border border-sky-blue/30">
                  Quick Response
                </span>
                <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-medium border border-primary/30">
                  Expert Consultation
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
